// Branding colors are stored as hex, but the dashboard theme consumes
// `hsl(var(--token))`, so brand colors must be expressed as the "H S% L%"
// triplet the var expects. These helpers convert and pick a readable
// foreground for text drawn on a brand-colored surface.

function normalizeHex(hex?: string | null): string | null {
  if (!hex) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  if (h.length !== 6 || /[^0-9a-fA-F]/.test(h)) return null;
  return h.toLowerCase();
}

/** "#1673EA" -> "213 84% 50%" (the form `hsl(var(--primary))` expects). */
export function hexToHslParts(hex?: string | null): string | null {
  const h = normalizeHex(hex);
  if (!h) return null;
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  let s = 0;
  let hue = 0;
  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case r:
        hue = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        hue = (b - r) / d + 2;
        break;
      default:
        hue = (r - g) / d + 4;
        break;
    }
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

/** Black or white (as an HSL triplet) for legible text on a hex background. */
export function readableForeground(hex?: string | null): string {
  const h = normalizeHex(hex);
  if (!h) return '0 0% 100%';
  const channel = (c: number) => {
    const v = c / 255;
    return v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4);
  };
  const lum =
    0.2126 * channel(parseInt(h.slice(0, 2), 16)) +
    0.7152 * channel(parseInt(h.slice(2, 4), 16)) +
    0.0722 * channel(parseInt(h.slice(4, 6), 16));
  return lum > 0.5 ? '0 0% 10%' : '0 0% 100%';
}
